/**
 * Analytics Dashboard — for platform_admin, venue_admin, club_admin, tournament_admin.
 * Pulls from /api/analytics/overview and renders totals + recent activity + top players.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, Users, Trophy, MapPin, Building2,
  Activity, TrendingUp, Calendar, ArrowLeft,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { analyticsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';

const StatCard = ({ icon: Icon, label, value, hint, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-4 sm:p-5">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-secondary/10 flex items-center justify-center text-secondary">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="font-heading text-2xl font-extrabold tabular-nums">{value ?? '—'}</p>
          {hint && <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function Analytics() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [days, setDays] = useState('30');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    let cancelled = false;
    setLoading(true);
    analyticsAPI.overview(parseInt(days, 10))
      .then(r => { if (!cancelled) { setData(r.data); setError(null); } })
      .catch(err => {
        if (cancelled) return;
        const msg = err.response?.data?.detail || 'Unable to load analytics';
        setError(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days, isAuthenticated, navigate]);

  const role = data?.role || user?.role;
  const isPlatform = role === 'platform_admin';
  const totals = data?.totals || {};
  const maxDaily = Math.max(1, ...(data?.daily_activity || []).map(d => d.count));

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6" data-testid="analytics-page">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-9 w-9" data-testid="analytics-back">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-extrabold flex items-center gap-2">
                <BarChart3 className="h-6 w-6 text-secondary" />
                Analytics Dashboard
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isPlatform
                  ? 'Platform-wide overview — users, tournaments, matches.'
                  : `Scoped view for ${role?.replace('_', ' ') || 'your account'}.`}
              </p>
            </div>
          </div>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-36" data-testid="analytics-window-select">
              <Calendar className="h-3.5 w-3.5 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last 365 days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {error && (
          <Card className="border-destructive/40 bg-destructive/5">
            <CardContent className="p-4 text-sm text-destructive" data-testid="analytics-error">{error}</CardContent>
          </Card>
        )}

        {/* Totals */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Card key={i}><CardContent className="p-4"><Skeleton className="h-14 w-full" /></CardContent></Card>
            ))
          ) : (
            <>
              {isPlatform && (
                <StatCard testId="stat-users" icon={Users} label="Total Users" value={totals.users}
                          hint={totals.new_users_in_window ? `+${totals.new_users_in_window} new in window` : null} />
              )}
              <StatCard testId="stat-tournaments" icon={Trophy} label="Tournaments" value={totals.tournaments} />
              <StatCard testId="stat-matches" icon={Activity} label="Matches" value={totals.matches} />
              {isPlatform ? (
                <StatCard testId="stat-venues" icon={MapPin} label="Venues" value={totals.venues} />
              ) : (
                <StatCard testId="stat-venues" icon={MapPin} label="My Venues" value={totals.venues} />
              )}
              {isPlatform && (
                <StatCard testId="stat-clubs" icon={Building2} label="Clubs" value={totals.clubs} />
              )}
              {isPlatform && totals.live_now > 0 && (
                <StatCard testId="stat-live" icon={TrendingUp} label="Live Now" value={totals.live_now} />
              )}
            </>
          )}
        </div>

        {/* Daily activity */}
        {isPlatform && data?.daily_activity?.length > 0 && (
          <Card data-testid="daily-activity-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-secondary" /> Daily Match Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-1 h-32">
                {data.daily_activity.map(d => (
                  <div
                    key={d.day}
                    className="flex-1 min-w-[8px] bg-secondary/60 hover:bg-secondary rounded-t transition-colors group relative"
                    style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: '4px' }}
                    title={`${d.day}: ${d.count} matches`}
                    data-testid={`activity-bar-${d.day}`}
                  >
                    <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                      {d.count}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-[9px] text-muted-foreground mt-2">
                <span>{data.daily_activity[0]?.day}</span>
                <span>{data.daily_activity[data.daily_activity.length - 1]?.day}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Tournaments */}
        <Card data-testid="recent-tournaments-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="h-4 w-4 text-secondary" /> Recent Tournaments
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
            ) : !data?.recent_tournaments?.length ? (
              <p className="text-sm text-muted-foreground text-center py-6">No tournaments yet.</p>
            ) : (
              <div className="divide-y divide-border">
                {data.recent_tournaments.map((t, i) => (
                  <button
                    key={t.tournament_id}
                    onClick={() => navigate(`/tournaments/${t.tournament_id}`)}
                    className="w-full py-2.5 flex items-center justify-between gap-3 text-left hover:bg-muted/30 -mx-3 px-3 rounded transition-colors"
                    data-testid={`recent-tournament-${i}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">{t.name}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {t.format} · {t.team_count} teams · {new Date(t.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <Badge variant={t.status === 'completed' ? 'default' : t.status === 'in_progress' ? 'secondary' : 'outline'} className="text-[9px] shrink-0">
                      {t.status}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Players (platform admin only) */}
        {isPlatform && (
          <Card data-testid="top-players-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-secondary" /> Top Players (by ELO)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
              ) : !data?.top_players?.length ? (
                <p className="text-sm text-muted-foreground text-center py-6">No player stats yet.</p>
              ) : (
                <div className="divide-y divide-border">
                  {data.top_players.map((p, i) => {
                    const winRate = p.matches_played ? Math.round((p.matches_won / p.matches_played) * 100) : 0;
                    return (
                      <div key={p.user_id} className="py-2 flex items-center gap-3" data-testid={`top-player-${i}`}>
                        <span className="font-heading text-sm font-extrabold text-muted-foreground w-6 text-center">{i + 1}</span>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm truncate">{p.name}</div>
                          <div className="text-[10px] text-muted-foreground">{p.matches_played} matches · {winRate}% win rate</div>
                        </div>
                        <Badge variant="outline" className="font-mono text-xs tabular-nums">{p.elo}</Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
